import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { Button } from './ui/Button';
import { Logo } from './ui/Logo';
import { FloatingContact } from './FloatingContact';
import { RentreeBanner } from './RentreeBanner';
import { CFA_INFO, SITE_LAST_UPDATED, QUALIOPI_LOGO, PHONE_DISPLAY, PHONE_HREF, WHATSAPP_HREF } from '../data/constants';
const NAV_LINKS = [{
  name: 'Accueil',
  path: '/'
}, {
  name: 'Notre CFA',
  path: '/le-cfa'
}, {
  name: 'Vie étudiante',
  path: '/bde'
}, {
  name: 'CAP 360',
  path: '/candidater#cap-360-section' // Redirige vers la section CAP 360
}, {
  name: 'Formations',
  path: '/formations'
}, {
  name: 'Offres',
  path: '/offres'
}, {
  name: 'Admissions',
  path: '/candidater'
}, {
  name: 'Entreprises',
  path: '/entreprises'
}, {
  name: 'Qualité & engagements',
  path: '/qualite'
}, {
  name: 'FAQ',
  path: '/faq'
}];
export function Layout() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  return <div className="min-h-screen flex flex-col bg-background font-sans" data-id="element-428" data-mp="13ucwka004mg6">
      <RentreeBanner data-id="element-429" data-mp="13wpmb9c0413s" />

      {/* Barre de contact */}
      <div className="bg-primary-dark text-white" data-id="element-430" data-mp="18t7qo83f5xfl">
        <div className="container flex flex-wrap items-center justify-center md:justify-between gap-x-6 gap-y-1 py-2 text-sm" data-id="element-431" data-mp="1dt2hycr3wp16">
          <p className="hidden md:block text-lavande" data-id="element-432" data-mp="2k9xnj0lydcwj">
            Une question sur nos formations ou l'alternance ? Contactez-nous
            directement.
          </p>
          <div className="flex items-center gap-5" data-id="element-433" data-mp="1zk3img1xob21">
            <a href={PHONE_HREF} className="flex items-center gap-2 font-bold hover:text-brand-orange transition-colors" data-id="element-434" data-mp="1ziiogrjj6u4p">
              <Phone className="w-4 h-4 text-brand-orange shrink-0" data-id="element-435" data-mp="29it1ab1nlzjc" />
              {PHONE_DISPLAY}
            </a>
            <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-bold hover:text-brand-orange transition-colors" data-id="element-436" data-mp="24k828ey51lwh">
              <MessageCircle className="w-4 h-4 text-brand-orange shrink-0" data-id="element-437" data-mp="1bwc4d6wu1ny4" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-id="element-438" data-mp="1lrx571q60i6a">
        <div className="container flex h-20 items-center justify-between" data-id="element-439" data-mp="y0rq4os1ttx1">
          <Logo data-id="element-440" data-mp="t3rii9fjwj3t" />

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" data-id="element-441" data-mp="34qv15l80jdvm">
            {NAV_LINKS.map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium transition-colors hover:text-primary ${location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path) ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-442" data-mp="39ilhc7tmsvuy">
                {link.name}
              </Link>)}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3" data-id="element-443" data-mp="3sg65tdshhsf1">
            <a href={PHONE_HREF} className="group flex items-center gap-2 text-primary-dark hover:text-brand-orange transition-colors" data-id="element-444" data-mp="3ehxz4e5nbcqo">
              <span className="w-9 h-9 rounded-full bg-lavande flex items-center justify-center shrink-0 group-hover:bg-brand-orange/15 transition-colors" data-id="element-445" data-mp="2329c5mse6dpv">
                <Phone className="w-4 h-4 text-primary group-hover:text-brand-orange transition-colors" data-id="element-446" data-mp="274xnc14kvwq0" />
              </span>
              <span className="leading-tight" data-id="element-447" data-mp="2nck8slsjgpir">
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-448" data-mp="2iizjdi50h3v6">
                  Appelez-nous
                </span>
                <span className="font-bold text-sm whitespace-nowrap" data-id="element-449" data-mp="28m4pjcjonrn8">
                  {PHONE_DISPLAY}
                </span>
              </span>
            </a>
            <Button variant="outline" asChild data-id="element-450" data-mp="367iue5fg3uqy">
              <Link to="/entreprises" data-id="element-451" data-mp="31a8ws2hwkcs1">Recruter un alternant</Link>
            </Button>
            <Button asChild data-id="element-452" data-mp="2wb5rcgy0g4jj">
              <Link to="/candidater" data-id="element-453" data-mp="2wae2rq2tz72r">Candidater</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <Button variant="ghost" size="icon" aria-label="Menu" className="lg:hidden" onClick={() => setIsMobileMenuOpen(true)} data-id="element-454" data-mp="2s9kkbc85qkh9">
            <Menu className="h-6 w-6" data-id="element-455" data-mp="1gtdsizu6dywx" />
          </Button>
        </div>
      </header>

      {/* Custom Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 w-[100vw] min-h-[100dvh] overflow-y-auto isolate lg:hidden" style={{
      backgroundColor: '#FFFFFF',
      opacity: 1,
      zIndex: 99999
    }} data-id="element-456" data-mp="dgepujim4rh">
          <div className="flex flex-col min-h-full p-6" data-id="element-457" data-mp="a647me7djokx">
            {/* Header du menu */}
            <div className="flex items-center justify-between mb-8" data-id="element-458" data-mp="f2uo32n3cm6v">
              <Logo showTagline={false} data-id="element-459" data-mp="k15xjc9h4uok" />
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Fermer le menu" data-id="element-460" data-mp="k4bj4hiw9efj">
                <X className="h-8 w-8 text-primary-dark" data-id="element-461" data-mp="t0j8689c3pav" />
              </Button>
            </div>

            {/* Liens du menu */}
            <nav className="flex flex-col gap-6 flex-1" data-id="element-462" data-mp="2utqmbed7o0v6">
              {NAV_LINKS.map(link => <Link key={link.name} to={link.path} onClick={() => setIsMobileMenuOpen(false)} className={`text-2xl font-bold font-heading transition-colors hover:text-brand-orange ${location.pathname === link.path || link.path !== '/' && location.pathname.startsWith(link.path) ? 'text-primary' : 'text-primary-dark'}`} data-id="element-463" data-mp="1z9wycgsznz0">
                  {link.name}
                </Link>)}
            </nav>

            {/* Boutons d'action */}
            <div className="flex flex-col gap-4 mt-12 pb-8" data-id="element-464" data-mp="fz1fmqi9lsjc">
              <a href={PHONE_HREF} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-3 bg-lavande text-primary-dark font-bold rounded-xl py-5 text-lg" data-id="element-465" data-mp="trhfxjjs7cgk">
                <Phone className="w-5 h-5 text-primary" data-id="element-466" data-mp="13n193y6ynx1m" />
                {PHONE_DISPLAY}
              </a>
              <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center justify-center gap-3 bg-primary-dark text-white font-bold rounded-xl py-5 text-lg" data-id="element-467" data-mp="yo7t9z47btya">
                <MessageCircle className="w-5 h-5 text-brand-orange" data-id="element-468" data-mp="1dc82eheky93p" />
                Écrivez-nous sur WhatsApp
              </a>
              <Button size="lg" variant="outline" className="w-full justify-center border-primary/20 text-primary-dark hover:bg-primary/5 text-lg py-6" asChild onClick={() => setIsMobileMenuOpen(false)} data-id="element-469" data-mp="zkkypwib5yaw">
                <Link to="/entreprises" data-id="element-470" data-mp="umrrd059qy91">Recruter un alternant</Link>
              </Button>
              <Button size="lg" className="w-full justify-center bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-6 shadow-xl" asChild onClick={() => setIsMobileMenuOpen(false)} data-id="element-471" data-mp="gns2brdughcc">
                <Link to="/candidater" data-id="element-472" data-mp="w4rtaskx0iew">Candidater</Link>
              </Button>
            </div>
          </div>
        </div>}

      {/* Main Content */}
      <main className="flex-1" data-id="element-473" data-mp="r3klitirvj88">
        <Outlet data-id="element-474" data-mp="m6knn5w2tms7" />
      </main>

      {/* Footer */}
      <footer className="bg-primary-dark text-lavande pt-20 pb-10 border-t-4 border-primary" data-id="element-475" data-mp="m5sqhqmtzgzt">
        <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-476" data-mp="i6a6qy6qxbyk">
          {/* Identity */}
          <div className="space-y-6" data-id="element-477" data-mp="dbxm4ktj9ul8">
            <Logo light className="mb-2" data-id="element-478" data-mp="8ee91o8knnuv" />
            <div className="space-y-2 text-sm text-lavande/80" data-id="element-479" data-mp="8f76fyln510c">
              <p className="font-bold text-white" data-id="element-480" data-mp="3gm7e2i4kndh">{CFA_INFO.legalName}</p>
              <p data-id="element-481" data-mp="3ulw0i31f1hrb">SIRET : {CFA_INFO.siret}</p>
              <p data-id="element-482" data-mp="3ukuej3iv3aut">UAI : {CFA_INFO.uai}</p>
              <p data-id="element-483" data-mp="3unyrapae7ex3">
                Numéro de déclaration d'activité enregistrée sous le numéro{' '}
                {CFA_INFO.nda}. Cet enregistrement ne vaut pas agrément de
                l'État.
              </p>
              <p data-id="element-484" data-mp="2a3tjp8ef3emk">Président : {CFA_INFO.president}</p>
            </div>

            {/* Qualiopi certificate */}
            <div className="flex pt-2" data-id="element-485" data-mp="2f2o94t6w41fn">
              <span className="inline-flex items-center justify-center bg-white rounded-xl p-4 shadow-sm" data-id="element-486" data-mp="2f3zeuyjwbj9e">
                <img src={QUALIOPI_LOGO} alt="Certification Qualiopi — processus certifié, République Française" className="w-44 h-auto object-contain" data-id="element-487" data-mp="2k06niz3of8v1" />
              </span>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-6" data-id="element-488" data-mp="1walshhu90l5e">
            <h3 className="font-heading font-bold text-lg text-white" data-id="element-489" data-mp="213x2frucxi1j">
              Contact
            </h3>
            <ul className="space-y-4 text-sm" data-id="element-490" data-mp="13osv5nrq1yq0">
              <li className="flex items-start gap-3" data-id="element-491" data-mp="13lwx7zfvja1k">
                <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center shrink-0" data-id="element-492" data-mp="18m2imxvjw0b7">
                  <MapPin className="w-4 h-4 text-white" data-id="element-493" data-mp="isubzz2tdu0w" />
                </div>
                <span className="pt-1.5" data-id="element-494" data-mp="nrxi7muj66ag">{CFA_INFO.address}</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-495" data-mp="ss36ct1ldans">
                <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center shrink-0" data-id="element-496" data-mp="ssl6i0rdk90z">
                  <Phone className="w-4 h-4 text-white" data-id="element-497" data-mp="xotrhus96wg2" />
                </div>
                <a href={PHONE_HREF} className="font-bold text-white hover:text-brand-orange transition-colors" data-id="element-498" data-mp="4pc52pa2t7d">
                  {PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-499" data-mp="es269ft5i79d">
                <div className="w-8 h-8 rounded-full bg-primary/40 flex items-center justify-center shrink-0" data-id="element-500" data-mp="etli8u7rxw5c">
                  <Mail className="w-4 h-4 text-white" data-id="element-501" data-mp="3dhskf3uh0sff" />
                </div>
                <a href={`mailto:${CFA_INFO.email}`} className="hover:text-white transition-colors font-medium" data-id="element-502" data-mp="3ifua47mejpzv">
                  {CFA_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Navigation */}
          <div className="space-y-6" data-id="element-503" data-mp="1qk1hvt1gi9zu">
            <h3 className="font-heading font-bold text-lg text-white" data-id="element-504" data-mp="1qn5nmuukldsm">
              Navigation
            </h3>
            <ul className="space-y-3 text-sm font-medium" data-id="element-505" data-mp="1gmwqnesr7oc6">
              <li data-id="element-506" data-mp="1gog6j4ahzxdf">
                <Link to="/" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-507" data-mp="1gozrktvg31vo">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-508" data-mp="17vmox07kwlwe" /> Accueil
                </Link>
              </li>
              <li data-id="element-509" data-mp="12xl68ge2eolg">
                <Link to="/le-cfa" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-510" data-mp="12txarbp27s43">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-511" data-mp="syvl8eqvgo0z" /> Le CFA
                </Link>
              </li>
              <li data-id="element-512" data-mp="j8ne5jaurbdh">
                <Link to="/bde" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-513" data-mp="jcb5qcwqlnpk">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-514" data-mp="aalfhaelcutd" /> Le BDE
                </Link>
              </li>
              <li data-id="element-515" data-mp="eb5bubrcbueg">
                <Link to="/formations" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-516" data-mp="ecota5oqgbht">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-517" data-mp="16z16zc19he94" /> Nos
                  formations
                </Link>
              </li>
              <li data-id="element-518" data-mp="x9q07w1ukloe">
                <Link to="/offres" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-519" data-mp="x97zam48pja5">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-520" data-mp="1kxhcx0rav0q7" /> Offres
                  d'alternance
                </Link>
              </li>
              <li data-id="element-521" data-mp="3afe84zgw00z4">
                <Link to="/candidater" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-522" data-mp="3aev14dsrywzv">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-523" data-mp="3k6shs02jyh5z" />{' '}
                  Candidater
                </Link>
              </li>
              <li data-id="element-524" data-mp="3f7xw8qd7aei6">
                <Link to="/entreprises" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-525" data-mp="3f9iiztt9wt5h">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-526" data-mp="2mk1rcydh9gab" /> Espace
                  Entreprises
                </Link>
              </li>
              <li data-id="element-527" data-mp="2wgy3de2l4kjm">
                <Link to="/contact" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-528" data-mp="2wgeusnytr8i9">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-529" data-mp="grd8x25wudmf" /> Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Informations utiles */}
          <div className="space-y-6" data-id="element-530" data-mp="2hgsrs0cx7mv4">
            <h3 className="font-heading font-bold text-lg text-white" data-id="element-531" data-mp="2mdaz97zmxzb5">
              Informations utiles
            </h3>
            <ul className="space-y-3 text-sm font-medium" data-id="element-532" data-mp="2qemovdiiekjc">
              <li data-id="element-533" data-mp="2ve939qa2q6tq">
                <Link to="/faq" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-534" data-mp="2ver7b92z9pc7">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-535" data-mp="30brddjztus3b" />{' '}
                  Questions fréquentes
                </Link>
              </li>
              <li data-id="element-536" data-mp="3a4gepm1nypkx">
                <Link to="/qualite" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-537" data-mp="3a46w4jiy7kp9">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-538" data-mp="3j4uyui2w5ymu" /> Qualité
                  & Qualiopi
                </Link>
              </li>
              <li data-id="element-539" data-mp="3o1274hirfm7y">
                <Link to="/accessibilite" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-540" data-mp="21pgvzgc27hp">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-541" data-mp="3t6p5769x6q5s" />{' '}
                  Accessibilité Handicap
                </Link>
              </li>
              <li data-id="element-542" data-mp="3k83shrr4q5cx">
                <Link to="/reclamations" className="hover:text-white transition-colors flex items-center gap-2" data-id="element-543" data-mp="3k8n9n68ygppw">
                  <ChevronRight className="w-3 h-3 text-brand-orange" data-id="element-544" data-mp="fsltwhxs0g3n" />{' '}
                  Réclamations
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="container pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-lavande/60" data-id="element-545" data-mp="711z26w9eqdy">
          <div className="flex flex-col items-center md:items-start gap-1" data-id="element-546" data-mp="13k9gworx370n">
            <p className="font-medium" data-id="element-547" data-mp="yqx030thc4ig">
              © {new Date().getFullYear()} {CFA_INFO.name}. Tous droits
              réservés.
            </p>
            <p className="text-lavande/50" data-id="element-548" data-mp="2t26c4nhi879k">
              Site mis à jour le {SITE_LAST_UPDATED}
            </p>
          </div>
          <div className="flex items-center gap-6 font-medium" data-id="element-549" data-mp="2jajyylllvy46">
            <Link to="/mentions-legales" className="hover:text-white transition-colors" data-id="element-550" data-mp="2j867emq4x41d">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="hover:text-white transition-colors" data-id="element-551" data-mp="2a811yi4i4fpu">
              Politique de confidentialité
            </Link>
            <Link to="/cgv" className="hover:text-white transition-colors" data-id="element-552" data-mp="20cplnvdg30fz">
              CGV
            </Link>
          </div>
        </div>
      </footer>

      <FloatingContact data-id="element-553" data-mp="3uonw20nhq2dg" />
    </div>;
}